import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowUpRight, ArrowRight } from "lucide-react";
import Seo from "@/components/Seo";
import { Reveal, Eyebrow } from "@/components/Reveal";
import { api } from "@/lib/api";

const FILTERS = ["All", "Web Development", "Graphic Design", "Social Media", "Digital Marketing"];

export default function Portfolio() {
    const [active, setActive] = useState("All");
    const [items, setItems] = useState([]);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        setLoading(true);
        const q = active === "All" ? "" : `?category=${encodeURIComponent(active)}`;
        api.get(`/portfolio${q}`)
            .then((r) => setItems(r.data))
            .catch(() => setItems([]))
            .finally(() => setLoading(false));
    }, [active]);

    return (
        <main className="pt-32 sm:pt-40 pb-24">
            <Seo title="Our Work" path="/portfolio" description="Portfolio of web development, graphic design, social media, and digital marketing concepts by Innovative Fusion Hives, Long Beach, CA." />
            <section className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16" data-testid="portfolio-hero">
                <Reveal>
                    <Eyebrow>Our Work</Eyebrow>
                    <h1 className="mt-4 max-w-3xl font-display text-4xl sm:text-5xl lg:text-6xl font-extrabold tracking-tight text-white leading-[1.05]">
                        Work from the hive.
                    </h1>
                    <p className="mt-6 max-w-2xl text-lg text-slate-300 leading-relaxed">
                        Explore our creative and technical capabilities. Entries marked <span className="text-amber-300 font-semibold">Concept</span> are demonstration projects shown while real client engagements are prepared for publication.
                    </p>
                </Reveal>
                <Reveal delay={0.1}>
                    <div className="mt-10 flex flex-wrap gap-2.5" role="tablist" aria-label="Portfolio filters">
                        {FILTERS.map((f) => (
                            <button key={f} role="tab" aria-selected={active === f}
                                data-testid={`portfolio-filter-${f.toLowerCase().replace(/\s+/g, "-")}`}
                                onClick={() => setActive(f)}
                                className={`rounded-full px-5 py-2.5 text-sm font-semibold transition-all ${active === f
                                    ? "bg-violet-600 text-white shadow-[0_0_20px_rgba(124,58,237,0.4)]"
                                    : "border border-white/10 text-slate-400 hover:border-violet-500/50 hover:text-white"}`}>
                                {f}
                            </button>
                        ))}
                    </div>
                </Reveal>
            </section>

            <section className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16 mt-12" data-testid="portfolio-grid">
                {loading ? (
                    <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                        {[1, 2, 3].map((n) => <div key={n} className="aspect-[4/3] rounded-2xl border border-white/5 bg-hive-card animate-pulse" />)}
                    </div>
                ) : items.length === 0 ? (
                    <div className="rounded-2xl border border-white/10 bg-hive-card p-14 text-center" data-testid="portfolio-empty-state">
                        <p className="font-display text-xl font-bold text-white">No projects in this category yet.</p>
                        <p className="mt-2 text-sm text-slate-400">New work is added regularly — check back soon or start a project of your own.</p>
                    </div>
                ) : (
                    <div className="grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
                        {items.map((p, i) => (
                            <Reveal key={p.id} delay={(i % 3) * 0.07}>
                                <Link to={`/portfolio/${p.slug}`} data-testid={`portfolio-card-${p.slug}`}
                                    className="group block overflow-hidden rounded-2xl border border-white/10 bg-hive-card transition-all hover:border-violet-500/50 hover:-translate-y-1 hover:shadow-[0_16px_48px_rgba(124,58,237,0.15)]">
                                    <div className="relative overflow-hidden aspect-[4/3]">
                                        <img src={p.cover_image} alt={`${p.title} — ${p.category}`} loading="lazy"
                                            className="h-full w-full object-cover transition-transform duration-700 group-hover:scale-105" />
                                        {p.is_sample && (
                                            <span className="absolute left-3 top-3 rounded-full border border-amber-400/40 bg-black/70 px-3 py-1 font-mono2 text-[10px] uppercase tracking-widest text-amber-300">Concept</span>
                                        )}
                                    </div>
                                    <div className="p-6">
                                        <p className="font-mono2 text-[10px] uppercase tracking-[0.25em] text-cyan-400">{p.category}</p>
                                        <h2 className="mt-2 font-display text-lg font-bold text-white leading-snug">{p.title}</h2>
                                        <p className="mt-2 line-clamp-2 text-sm text-slate-400">{p.description}</p>
                                        <span className="mt-4 inline-flex items-center gap-1.5 text-xs font-semibold text-violet-300">
                                            View project <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5" />
                                        </span>
                                    </div>
                                </Link>
                            </Reveal>
                        ))}
                    </div>
                )}
            </section>

            <section className="mx-auto max-w-7xl px-4 sm:px-8 lg:px-16 mt-24" data-testid="portfolio-cta">
                <Reveal>
                    <div className="rounded-2xl border border-violet-500/25 bg-hive-card p-10 sm:p-14 text-center">
                        <h2 className="font-display text-3xl font-bold tracking-tight text-white">Have a similar project in mind?</h2>
                        <p className="mx-auto mt-4 max-w-xl text-sm text-slate-400">Tell us about it — we will respond with next steps during business hours.</p>
                        <Link to="/contact" data-testid="portfolio-cta-button"
                            className="mt-8 inline-flex items-center gap-2 rounded-full bg-violet-600 px-8 py-3.5 font-semibold text-white transition-all hover:bg-violet-500 hover:shadow-[0_0_32px_rgba(124,58,237,0.5)]">
                            Start Your Project <ArrowUpRight className="h-4 w-4" />
                        </Link>
                    </div>
                </Reveal>
            </section>
        </main>
    );
}
